import React, { ChangeEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cloneDeep, escapeRegExp, isEmpty, omit, size, sumBy } from 'lodash';
// import numeral from 'numeral';
import { Button, Dropdown, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';

import { FilterDefinition, FilterValue } from 'common/filters/Filters.types';

import 'common/filters/filter-bar/multi-select-filter/MultiSelectFilter.scss';

interface Props {
    filter: FilterDefinition;
    focusClass?: string;
    showSearch?: boolean;
    searchValue?: string;
    show?: boolean;
    showSelectAll?: boolean;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    closeDropdown?: () => void;
    onDraftChange?: (values: FilterValue[]) => void;
}

type HeaderSlot = 'search' | 'all' | 'none';

export const MultiSelectFilter = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const { t } = useTranslation(['common/filters']);

    const [search, setSearch] = useState('');
    const [draftValues, setDraftValues] = useState<FilterValue[]>([]);
    const [bucketSnapshot, setBucketSnapshot] = useState<FilterValue[]>([]);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [scrollTop, setScrollTop] = useState<number>();

    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const allRef = useRef<HTMLButtonElement>(null);
    const noneRef = useRef<HTMLButtonElement>(null);
    const rafId = useRef<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedListRef = useRef<List | null>(null);
    const unselectedListRef = useRef<List | null>(null);

    const showSelectAll = props.showSelectAll === undefined ? true : props.showSelectAll;

    const headerSlots = useMemo<HeaderSlot[]>(() => {
        const slots: HeaderSlot[] = [];

        if (props.showSearch) {
            slots.push('search');
        }

        if (showSelectAll) {
            slots.push('all');
            slots.push('none');
        } else {
            slots.push('none');
        }

        return slots;
    }, [props.showSearch, showSelectAll]);

    const headerCount = headerSlots.length;

    useEffect(() => {
        if (!props.show) return;

        setSearch('');
        setDraftValues(cloneDeep(props.filter.values));
        setBucketSnapshot(cloneDeep(props.filter.values));
        setScrollTop(0);
    }, [props.show, props.filter.values]);

    const selectAll = (selected: boolean): void => {
        setDraftValues((prev) => {
            const next = prev.map((value) => (filterValues(value) ? { ...value, selected } : value));

            props.onDraftChange?.(next);
            return next;
        });
    };

    const selectFilterValue = (filterValue: FilterValue): void => {
        setDraftValues((prev) => {
            const next = prev.map((value) =>
                value.value === filterValue.value ? { ...value, selected: !value.selected } : value,
            );

            props.onDraftChange?.(next);
            return next;
        });
    };

    const filterValues = useCallback(
        (filterValue: FilterValue): boolean => {
            let isMatch = true;
            const searchValue = props.searchValue || search;

            if (!isEmpty(searchValue)) {
                const pattern = new RegExp(escapeRegExp(searchValue), 'i');

                isMatch = pattern.test(filterValue.label) || pattern.test(filterValue.value);
            }

            return isMatch;
        },
        [props.searchValue, search],
    );

    const getDraftValueByValue = useCallback(
        (value: string) => draftValues.find((item) => item.value === value),
        [draftValues],
    );

    const renderList = (filterValues: FilterValue[], isSelectedBucket: boolean): JSX.Element => {
        const width = 200;
        const rowCount = size(filterValues);
        const lineHeight = 28;
        const singleLineCharLength = 24;
        const height = Math.min(
            isSelectedBucket ? 145 : 250,
            lineHeight *
                (rowCount < 10
                    ? sumBy(filterValues, (filterValue) => {
                          return size(filterValue.label) <= singleLineCharLength ? 1 : 1.7;
                      })
                    : rowCount),
        );
        const cache = new CellMeasurerCache({
            minHeight: 28,
            fixedWidth: true,
        });

        const rowRenderer = ({ index, style, parent }: { index: number; style: any; parent: any }): JSX.Element => {
            const bucketValue = filterValues[index];
            const draftValue = getDraftValueByValue(bucketValue.value) ?? bucketValue;
            const { focusClass } = props;
            const absoluteIndex = isSelectedBucket ? headerCount + index : headerCount + selectedCount + index;

            if (!draftValue) {
                return (
                    <Dropdown.Item className="text-muted text-center" key="no-values" style={style}>
                        {t('NO_FILTER_VALUES')}
                    </Dropdown.Item>
                );
            }

            return (
                <CellMeasurer cache={cache} columnIndex={0} parent={parent} rowIndex={index} key={bucketValue.value}>
                    {({ measure }) => (
                        <div
                            id={`${isSelectedBucket ? 'selected' : 'unselected'}-row-${index}`}
                            data-focus-index={absoluteIndex}
                            role="checkbox"
                            aria-checked={draftValue.selected}
                            data-child-option="true"
                            className={`dropdown-item d-flex align-items-center gap-2 ${
                                index === 0 && !isSelectedBucket && focusClass ? focusClass : ''
                            }`}
                            key={bucketValue.value}
                            style={omit(style, 'height')}
                            onClick={(e): void => {
                                e.stopPropagation();
                                selectFilterValue(draftValue);
                            }}
                            onFocus={(): void => setFocusedIndex(absoluteIndex)}
                            tabIndex={-1}
                            onKeyDown={(e): void => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    selectFilterValue(draftValue);
                                }
                            }}
                            aria-label={bucketValue.label}
                        >
                            <input type="checkbox" checked={!!draftValue.selected} tabIndex={-1} aria-hidden="true" />

                            <span>{bucketValue.label}</span>
                        </div>
                    )}
                </CellMeasurer>
            );
        };

        return (
            <List
                className={`${isSelectedBucket ? 'selected' : 'unselected'}-list ${
                    isSelectedBucket && size(visibleUnselected) ? 'bordered' : ''
                }`}
                height={height}
                width={width}
                rowCount={rowCount}
                rowHeight={cache.rowHeight}
                rowRenderer={rowRenderer}
                values={filterValues}
                scrollTop={scrollTop}
                onScroll={(): void => {
                    setScrollTop(undefined);
                }}
                aria-label=""
                role="presentation"
                containerRole="presentation"
                ref={isSelectedBucket ? selectedListRef : unselectedListRef}
            />
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            if (totalFocusableCount === 0) return;

            e.preventDefault();

            setFocusedIndex((prev) => {
                if (e.key === 'ArrowDown') {
                    return (prev + 1) % totalFocusableCount;
                }
                return (prev - 1 + totalFocusableCount) % totalFocusableCount;
            });
            return;
        }

        props.onKeyDown?.(e);
    };

    const visibleSelected = useMemo(
        () => bucketSnapshot.filter((value) => value.selected).filter(filterValues),
        [bucketSnapshot, filterValues],
    );

    const visibleUnselected = useMemo(
        () => bucketSnapshot.filter((value) => !value.selected).filter(filterValues),
        [bucketSnapshot, filterValues],
    );

    const selectedCount = visibleSelected.length;
    const unselectedCount = visibleUnselected.length;
    const visibleCount = selectedCount + unselectedCount;

    // Total focusable elements in dropdown
    const totalFocusableCount = headerCount + selectedCount + unselectedCount;

    const focusHeader = useCallback(
        (index: number) => {
            const slot = headerSlots[index];

            switch (slot) {
                case 'search':
                    searchInputRef.current?.focus();
                    break;
                case 'all':
                    allRef.current?.focus();
                    break;
                case 'none':
                    noneRef.current?.focus();
                    break;
                default:
                    // Should never happen
                    break;
            }
        },
        [headerSlots],
    );

    const focusVirtualRow = useCallback((absoluteIndex: number) => {
        const el = containerRef.current?.querySelector(`[data-focus-index="${absoluteIndex}"]`) as HTMLElement | null;

        el?.focus();
    }, []);

    useEffect(() => {
        const cleanup = () => {
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
                rafId.current = null;
            }
        };

        cleanup();

        if (focusedIndex < 0) {
            return cleanup;
        }

        if (focusedIndex < headerCount) {
            focusHeader(focusedIndex);
            return cleanup;
        }

        const listIndex = focusedIndex - headerCount;

        if (listIndex < selectedCount) {
            selectedListRef.current?.scrollToRow(listIndex);

            rafId.current = requestAnimationFrame(() => {
                focusVirtualRow(focusedIndex);
            });
        } else {
            const unselectedIndex = listIndex - selectedCount;

            unselectedListRef.current?.scrollToRow(unselectedIndex);

            rafId.current = requestAnimationFrame(() => {
                focusVirtualRow(focusedIndex);
            });
        }

        return cleanup;
    }, [focusedIndex, headerCount, selectedCount, unselectedCount, focusHeader, focusVirtualRow]);

    // useEffect(() => {
    //     if (props.show) {
    //         setFocusedIndex(0);
    //     }
    // }, [props.show]);

    return (
        <Dropdown.Menu
            data-filter-menu="true"
            className="r4e-multi-select-filter"
            data-attr-name={props.filter.name}
            ref={ref}
            role={undefined}
            onKeyDownCapture={handleKeyDown}
        >
            <div
                ref={containerRef}
                role="group"
                aria-label={`${props.filter.label} multi-select filter`}
                aria-describedby={`${props.filter.name}-instructions ${props.filter.name}-count`}
            >
                <div className="dropdown-header" role="presentation">
                    {props.showSearch && (
                        <div className="search-wrapper" role="presentation">
                            <label className="sr-only" htmlFor={`${props.filter.name}-search`}>
                                {t('SEARCH')}
                            </label>
                            <i className="icon-search" aria-hidden="true" />
                            <FormControl
                                id={`${props.filter.name}-search`}
                                ref={searchInputRef}
                                value={search}
                                data-child-search="true"
                                aria-label={t('FILTER_ITEMS_COUNT', { visibleOptionCount: visibleCount })}
                                onChange={(e: ChangeEvent<HTMLInputElement>): void => setSearch(e.target.value)}
                                onFocus={() => setFocusedIndex(headerSlots.indexOf('search'))}
                            />
                        </div>
                    )}
                    {showSelectAll ? (
                        <>
                            <span className="select">{t('SELECT')}</span>
                            <Button
                                ref={allRef}
                                variant="link"
                                onClick={(): void => selectAll(true)}
                                aria-label={t('SELECT_ALL_VISIBLE_ITEMS', { filterLabel: props.filter.label })}
                                data-child-bulk-all="true"
                                onFocus={() => setFocusedIndex(headerSlots.indexOf('all'))}
                            >
                                {t('ALL')}
                            </Button>
                            <span className="separator">|</span>
                            <Button
                                ref={noneRef}
                                variant="link"
                                onClick={(): void => selectAll(false)}
                                aria-label={t('CLEAR_ALL_VISIBLE_ITEMS', { filterLabel: props.filter.label })}
                                data-child-bulk-none="true"
                                onFocus={() => setFocusedIndex(headerSlots.indexOf('none'))}
                            >
                                {t('NONE')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <span className="select">{t('SELECT')}</span>{' '}
                            <Button
                                ref={noneRef}
                                variant="link"
                                onClick={(): void => selectAll(false)}
                                aria-label={t('CLEAR_ALL_VISIBLE_ITEMS', { filterLabel: props.filter.label })}
                                data-child-bulk-none="true"
                                onFocus={() => setFocusedIndex(headerSlots.indexOf('none'))}
                            >
                                {t('NONE')}
                            </Button>
                        </>
                    )}
                </div>
                {selectedCount > 0 && renderList(visibleSelected, true)}
                {unselectedCount > 0 && renderList(visibleUnselected, false)}
                {props.children}
            </div>
        </Dropdown.Menu>
    );
});
